import React from 'react';
import { Calendar } from 'lucide-react';

// (5 Oct 2026) Help page: "Events not appearing in your calendar?"
// Opened from the link at the end of every WhatsApp event message
// (App.js buildWaNotifyMessage): https://calendar.telematrixconsulting.com/?help=subscribe
// App.js shows this page before anything else, so it works signed in or not.
// Instructions only: never put a feed link on this page.

// The logo shown at the top. If the file is missing, the calendar icon used
// on the loading screen is shown instead, so the page never shows a broken image.
const LOGO_SRC = '/Calendar_Hub_Logo.png';

const Section = ({ number, title, children }) => (
  <section className="flex gap-3 sm:gap-4">
    <div className="shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center">
      {number}
    </div>
    <div className="min-w-0 pt-0.5">
      <h2 className="font-semibold text-gray-800 mb-1.5">{title}</h2>
      <div className="space-y-2 text-gray-700 leading-relaxed">{children}</div>
    </div>
  </section>
);

const HelpPage = () => {
  const [logoFailed, setLogoFailed] = React.useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 px-4 py-6 sm:py-10">
      <div className="max-w-2xl mx-auto">
        <header className="flex items-center gap-3 mb-6">
          {logoFailed ? (
            <div className="bg-blue-100 p-3 rounded-full">
              <Calendar className="w-8 h-8 text-blue-600" />
            </div>
          ) : (
            <img src={LOGO_SRC} alt="" className="w-14 h-14 object-contain" onError={() => setLogoFailed(true)} />
          )}
          <span className="text-2xl font-bold text-gray-800">Calendar Hub</span>
        </header>

        <main className="bg-white rounded-2xl shadow-sm p-5 sm:p-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">Events not appearing in your calendar?</h1>

          <div className="space-y-6">
            <Section number={1} title="Check you're subscribed.">
              <p>
                <span className="font-medium text-gray-800">iPhone or iPad:</span> open the Calendar app and tap
                Calendars at the bottom. The calendar should be listed under "Subscribed", with a tick beside it.
              </p>
              <p>
                <span className="font-medium text-gray-800">Google Calendar on a computer:</span> the calendar should
                be listed under "Other calendars", with its box ticked.
              </p>
              <p>
                <span className="font-medium text-gray-800">Google Calendar on Android:</span> calendars added from a
                link only appear once syncing is on. In the Calendar app, open Settings, tap the calendar's name and
                turn on Sync.
              </p>
            </Section>

            <Section number={2} title="Not subscribed yet?">
              <p>
                Open Calendar Hub, sign in, and at the bottom of the page tap "Subscribe to add this calendar to your
                phone, tablet and/or computer".
              </p>
              <p>
                Always subscribe from Calendar Hub with your own link. Don't use a link someone else has sent you.
              </p>
            </Section>

            <Section number={3} title="Changes can take a while to appear.">
              <p>
                iPhones check for changes at the interval set on your phone. Google can take several hours, sometimes
                up to a day. Calendar Hub itself always shows the latest.
              </p>
            </Section>

            <Section number={4} title="Still not right?">
              <p>Contact your calendar's administrator.</p>
            </Section>
          </div>

          <a
            href="/"
            className="mt-8 block w-full text-center bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 transition"
          >
            Open Calendar Hub
          </a>
        </main>
      </div>
    </div>
  );
};

export default HelpPage;
